import {
  BaseQueryApi,
  BaseQueryFn,
  createApi,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
  FetchBaseQueryMeta,
} from '@reduxjs/toolkit/query/react';
import { ClientOverviewApiResponse } from '../slices/overview';
import {
  Subscription,
  ApiSubscription,
  SubscriptionProfit,
  SubscriptionSummary,
} from '../../types/subscription';
import { Bot } from 'grts-utils/types/Bot';
import { PriceChartApiResponse } from '../../screens/Detail/Charts';
import { Exchange } from 'grts-utils/types/Exchange';
import { GetTopAssetsByVolumeInfo } from '../../types/Bot';
import { setUserId } from '../slices/user';
import {
  IndicatorDataPoint,
  IndicatorSingleValue,
  RSIItem,
  RsiValuesApiResponse,
  SingleProfitCalculationApiResponse,
} from '../../screens/ProfitCalculations/types';
import { CandleFetcherQueue } from '../../types/Queue';
import { persistor, RootState } from '../store';
import { PredefinedBasket } from '../../types/Baskets';
import { RSIConfig } from '../../screens/ProfitCalculations/Content/Form/RSI';
import { BasicParams } from 'grts-utils/types/ProfitCalcs';

const host = process.env.REACT_APP_HOST;
const env = process.env.NODE_ENV;
const isProduction = env === 'production';

const dataBaseUrl = isProduction
  ? `https://${host}/data`
  : `http://${host}:5010`;
const candleFetchBaseUrl = isProduction
  ? `https://${host}/candle-fetcher`
  : `http://${host}:5020`;
const profitCalcsBaseUrl = isProduction
  ? `https://${host}/profit-calcs`
  : `http://${host}:5000`;
const exchangeBaseUrl = isProduction
  ? `https://${host}/exchange`
  : `http://${host}:5050`;

// baseQuery for data service
const dataServiceQuery = fetchBaseQuery({
  baseUrl: dataBaseUrl,
  credentials: 'include',
});

// Custom baseQuery for candel fetcher service
const candlesServiceQuery = fetchBaseQuery({
  baseUrl: candleFetchBaseUrl,
});

// Custom baseQuery for profit calcs service
const profitServiceQuery = fetchBaseQuery({
  baseUrl: profitCalcsBaseUrl,
});

// Custom baseQuery for exchange service
const exchangeServiceQuery = fetchBaseQuery({
  baseUrl: exchangeBaseUrl,
});

const forceLogout = async (
  userId: number | null,
  api: BaseQueryApi,
  extraOptions: any,
) => {
  await dataServiceQuery(
    { url: '/client/logout', method: 'POST', body: { userId } },
    api,
    extraOptions,
  );

  persistor.purge(); // Clear persisted storage
  window.location.reload();
};

const baseQueryWithRefresh: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError,
  any,
  FetchBaseQueryMeta
> = async (args, api, extraOptions) => {
  let result = await dataServiceQuery(args, api, extraOptions);

  const userId = (api.getState() as RootState).user.id;
  const data = result.error?.data as any;

  // If one of the following errors occures, trying to refresh the access token will have no effect
  // Log out
  if (
    data?.error === 'Missing secrets' ||
    data?.error === 'Refresh token expired'
  ) {
    await forceLogout(userId, api, extraOptions);
    return result;
  }

  // Refresh the access token and try again
  if (data?.error === 'Token required' || data?.error === 'Invalid token') {
    const refreshResult = await dataServiceQuery(
      { url: '/token/refresh', method: 'POST', body: { userId } },
      api,
      extraOptions,
    );

    if (refreshResult.data) {
      result = await dataServiceQuery(args, api, extraOptions);
    } else {
      await forceLogout(userId, api, extraOptions);
      return { error: { status: 401, data: 'Session expired' } };
    }
  }

  return result;
};

export const api = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithRefresh,
  tagTypes: ['bot', 'subscription', 'exchange'],
  endpoints: (builder) => ({
    /** ==================== USERS ==================== */
    login: builder.mutation<
      {
        success: boolean;
        error?: string;
        userId: number;
        onboardingStep?: number;
        accessToken?: string;
      },
      { email: string; password: string }
    >({
      query: (postData) => ({
        url: '/client/login',
        method: 'POST',
        body: postData,
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          if (data.success && !data.onboardingStep) {
            dispatch(setUserId(data.userId));
          }
        } catch (error) {
          console.error('Query failed:', error);
        }
      },
    }),
    logout: builder.mutation<
      {
        success: boolean;
        error?: string;
        message?: string;
      },
      { userId: number | null }
    >({
      query: (postData) => ({
        url: '/client/logout',
        method: 'POST',
        body: postData,
      }),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          if (data.success) {
            persistor.purge(); // Clear persisted storage
            window.location.reload();
          }
        } catch (error) {
          console.error('Query failed:', error);
        }
      },
    }),
    createUser: builder.mutation<
      {
        success: boolean;
        error: string;
        userId: number;
      },
      { email: string; username: string; password: string }
    >({
      query: (postData) => ({
        url: '/client/user/create',
        method: 'POST',
        body: postData,
      }),
    }),
    getUserId: builder.query<number, void>({
      query: () => '/user',
    }),
    getUserAvailableCredits: builder.query<
      {
        success: boolean;
        credits: number;
      },
      number | null
    >({
      query: (userId: number) => `/client/credits/${userId}`,
    }),
    editAreNotificationsEnabled: builder.mutation<
      {
        success: boolean;
        error: string;
      },
      { userId: number | null; areNotificationsEnabled: boolean }
    >({
      query: (postData) => ({
        url: '/client/user/notifications',
        method: 'POST',
        body: postData,
      }),
    }),
    editUserOnboardingStep: builder.mutation<
      {
        success: boolean;
        error: string;
      },
      { userId: number | null; onboardingStep: number | null }
    >({
      query: (postData) => ({
        url: '/client/user/onboardingStep',
        method: 'POST',
        body: postData,
      }),
    }),
    /** ==================== OVERVIEW ==================== */
    getClientOverview: builder.query<ClientOverviewApiResponse, number>({
      query: (userId: number) => `/client/overview/${userId}`,
    }),
    /** ==================== SUBSCRIPTIONS ==================== */
    getSubscriptionDetails: builder.query<
      ApiSubscription,
      { userId: number | null; subscriptionId: number }
    >({
      providesTags: ['subscription'],
      query: ({ userId, subscriptionId }) =>
        `/client/sub/details/${userId}/${subscriptionId}`,
    }),
    getAllSubscriptionProfits: builder.query<
      Record<number, SubscriptionProfit>,
      number | null
    >({
      query: (userId) => `/client/subs/profit/${userId}`,
    }),
    getSubscriptionProfit: builder.query<
      SubscriptionProfit,
      { userId: number; subscriptionId: number }
    >({
      query: ({ userId, subscriptionId }) =>
        `/client/sub/profit/${userId}/${subscriptionId}`,
    }),
    getSubscriptionSummariesByUserId: builder.query<
      {
        success: boolean;
        subscriptionSummary: Array<SubscriptionSummary>;
      },
      number | null
    >({
      providesTags: ['exchange'],
      query: (userId: number) => `/client/subscription/summary/${userId}`,
    }),
    updateSubscriptionState: builder.mutation<
      {
        success: boolean;
        error: string;
        subscriptionId: number;
        isActive: boolean;
      },
      { userId: number | null; subscriptionId: number; isActive: boolean }
    >({
      query: (postData) => ({
        url: '/client/subscription/state',
        method: 'POST',
        body: postData,
      }),
    }),
    removeSubscription: builder.mutation<
      {
        success: boolean;
        error: string;
        subscriptionId: number;
      },
      { userId: number; subscriptionId: number; shouldSell: boolean }
    >({
      query: (postData) => ({
        url: '/client/subscription/remove',
        method: 'POST',
        body: postData,
      }),
    }),
    addSubscription: builder.mutation<
      { success: boolean; error?: string | undefined; subscriptionId?: number },
      {
        userId: number;
        basket: string;
        baseAsset: string;
        quoteAsset: string;
        exchange: Exchange;
        stableBalance: number;
        assetBalance: number;
        requestBot: boolean;
      }
    >({
      query: (postData) => ({
        url: '/client/subscription/add',
        method: 'POST',
        body: postData,
      }),
    }),
    updateSubscriptionPair: builder.mutation<
      {
        success: boolean;
        error: string;
      },
      {
        userId: number | null;
        subscriptionId: number;
        quoteAsset: string;
        exchange: Exchange;
        shouldMakeTransfer: boolean;
      }
    >({
      invalidatesTags: ['subscription'],
      query: (postData) => ({
        url: '/client/subscription/pair',
        method: 'POST',
        body: postData,
      }),
    }),
    /** ==================== BOTS ==================== */
    getBotIds: builder.query<
      { success: false; botIds: Array<number> },
      undefined
    >({
      query: () => '/client/bot/ids',
    }),
    getBot: builder.query<Bot, string>({
      providesTags: ['bot'],
      query: (botId: string) => `/client/bot/${botId}`,
    }),
    getTopBots: builder.query<
      {
        success: boolean;
        errorMessage?: string;
        assets?: Record<string, GetTopAssetsByVolumeInfo[]>;
      },
      { userId: number | null; query: string | undefined }
    >({
      query: ({ userId, query }) => `/client/bot/top/${userId}/${query}`,
    }),
    updateFeatureFlag: builder.mutation<
      { success: boolean },
      { botId: number; featureFlag: boolean }
    >({
      invalidatesTags: ['bot'],
      query: (postData) => ({
        url: '/bot/featureFlag',
        method: 'POST',
        body: postData,
      }),
    }),
    /** ==================== CANDLES ==================== */
    fetchHeikinCandles: builder.query<PriceChartApiResponse, any>({
      // Override the baseQuery for this specific endpoint
      queryFn: async (params, api) => {
        try {
          const result = await candlesServiceQuery(
            {
              url: 'fetch/candles/heikin',
              method: 'GET',
              params,
            },
            api,
            {},
          );

          if (result.error) {
            throw result.error;
          }

          return { data: result.data as PriceChartApiResponse };
        } catch (error) {
          return { error: error as any };
        }
      },
    }),
    /** ==================== BALANCE ==================== */
    getAvailableBalance: builder.query<
      { balance: number },
      { userId: number | null; subscriptionId: number; asset: string }
    >({
      query: ({
        userId,
        subscriptionId,
        asset,
      }: {
        userId: number | null;
        subscriptionId: number;
        asset: string;
      }) => `/client/balance/available/${userId}/${subscriptionId}/${asset}`,
    }),
    getAllAvailableBalancesFromExchanges: builder.query<
      {
        success: boolean;
        balances: Record<string, Record<string, number>>;
        error?: undefined;
      },
      number | null
    >({
      query: (userId) => `/client/balances/available/${userId}`,
    }),
    updateSubscriptionBalance: builder.mutation<void, any>({
      invalidatesTags: ['subscription'],
      query: (postData) => ({
        url: '/client/balance/update',
        method: 'POST',
        body: postData,
      }),
    }),
    /** ==================== EXCHANGE ==================== */
    getUserExchanges: builder.query<
      { enabledExchanges?: Exchange[]; allExchanges?: Exchange[] },
      number | null
    >({
      providesTags: ['exchange'],
      query: (userId: number | null) => `/client/exchanges/${userId}`,
    }),
    checkExchangeConnection: builder.mutation<
      { success: boolean; errorMessage?: string },
      {
        exchange: Exchange;
        key: string;
        secret: string;
        passphrase: string | undefined;
      }
    >({
      query: (postData) => ({
        url: '/client/exchange/test',
        method: 'POST',
        body: postData,
      }),
    }),
    addExchangeConnection: builder.mutation<
      { success: boolean; errorMessage?: string },
      {
        userId: number;
        exchange: Exchange;
        key: string;
        secret: string;
        passphrase?: string;
      }
    >({
      invalidatesTags: ['exchange'],
      query: (postData) => ({
        url: '/client/exchange/add',
        method: 'POST',
        body: postData,
      }),
    }),
    removeExchangeConnection: builder.mutation<
      { success: boolean; errorMessage?: string },
      {
        userId: number;
        exchange: Exchange;
      }
    >({
      invalidatesTags: ['exchange'],
      query: (postData) => ({
        url: '/client/exchange/remove',
        method: 'POST',
        body: postData,
      }),
    }),
    /** ==================== PROFIT ==================== */
    calculateSingleProfitCalculation: builder.query<
      SingleProfitCalculationApiResponse,
      any
    >({
      // Override the baseQuery for this specific endpoint
      queryFn: async (body, api) => {
        try {
          const result = await profitServiceQuery(
            {
              url: 'profit/hypothetical',
              method: 'POST',
              body,
            },
            api,
            {},
          );

          if (result.error) {
            throw result.error;
          }

          return { data: result.data as SingleProfitCalculationApiResponse };
        } catch (error) {
          return { error: error as any };
        }
      },
    }),
    calculateCurrentSingleProfitCalculation: builder.query<
      { data: SingleProfitCalculationApiResponse },
      string
    >({
      query: (baseAsset: string) => `/client/bot/profit/${baseAsset}`,
    }),
    startComplexParamsCalculation: builder.mutation<
      { didStart: boolean },
      { botFriendlyName: string; skipQueue: boolean }
    >({
      query: (postData) => ({
        url: '/calculations/add',
        method: 'POST',
        body: postData,
      }),
    }),
    /** ==================== QUEUE ==================== */
    getExchangeQueues: builder.query<
      {
        queues: Record<Exchange, Array<object>>;
      },
      any
    >({
      // Override the baseQuery for this specific endpoint
      queryFn: async (_, api) => {
        try {
          const result = await exchangeServiceQuery(
            {
              url: 'queues',
              method: 'GET',
            },
            api,
            {},
          );

          if (result.error) {
            throw result.error;
          }

          return {
            data: result.data as {
              queues: Record<Exchange, Array<object>>;
            },
          };
        } catch (error) {
          return { error: error as any };
        }
      },
    }),
    GetCandleFetcherQueues: builder.query<CandleFetcherQueue, any>({
      // Override the baseQuery for this specific endpoint
      queryFn: async (_, api) => {
        try {
          const result = await candlesServiceQuery(
            {
              url: 'queues',
              method: 'GET',
            },
            api,
            {},
          );

          if (result.error) {
            throw result.error;
          }

          return {
            data: result.data as CandleFetcherQueue,
          };
        } catch (error) {
          return { error: error as any };
        }
      },
    }),
    /** ==================== BASKETS ==================== */
    getAllPredefinedBaskets: builder.query<
      {
        predefinedBaskets: Array<PredefinedBasket>;
        assetInfo: Record<string, Array<GetTopAssetsByVolumeInfo>>;
      },
      number | null
    >({
      query: (userId) => `/baskets/predefined/${userId}`,
    }),
    getAllCopyMeBaskets: builder.query<
      {
        copyMeBaskets: Array<PredefinedBasket>;
        assetInfo: Record<string, Array<GetTopAssetsByVolumeInfo>>;
      },
      number | null
    >({
      query: (userId) => `/baskets/copyme/${userId}`,
    }),
    getCopyMeConfig: builder.query<
      Array<{
        friendlyName: string;
        isPublic: number;
      }>,
      number | null
    >({
      query: (userId) => `/baskets/copyme/config/${userId}`,
    }),
    addBasket: builder.mutation<
      {
        success: boolean;
        error?: string | undefined;
        subscriptionIds?: number[];
      },
      {
        userId: number;
        basket: string;
        bots: Array<{
          baseAsset: string;
          quoteAsset: string;
          exchange: Exchange;
          stableBalance: number;
          assetBalance: number;
        }>;
      }
    >({
      query: (postData) => ({
        url: '/client/basket/add',
        method: 'POST',
        body: postData,
      }),
    }),
    setUserCopyMe: builder.mutation<
      {
        success: boolean;
        error?: string | undefined;
      },
      {
        userId: number | null;
        value: boolean;
      }
    >({
      query: (postData) => ({
        url: '/baskets/copyme/user',
        method: 'POST',
        body: postData,
      }),
    }),
    setBasketCopyMe: builder.mutation<
      {
        success: boolean;
        error?: string | undefined;
      },
      {
        userId: number | null;
        basket: string;
        value: boolean;
      }
    >({
      query: (postData) => ({
        url: '/baskets/copyme/basket',
        method: 'POST',
        body: postData,
      }),
    }),
    /** ==================== INDICATORS ==================== */
    getRsiSignals: builder.query<
      { indicatorValues: RSIItem[]; signals: RSIItem[] },
      {
        basicParams: {
          baseAsset: string;
          quoteAsset: string;
          exchange: Exchange;
          startTimestamp: number;
          endTimestamp: number;
        };
        rsiConfig?: RSIConfig;
      }
    >({
      // Override the baseQuery for this specific endpoint
      queryFn: async (body, api) => {
        try {
          const result = await profitServiceQuery(
            {
              url: 'indicator/rsi',
              method: 'POST',
              body,
            },
            api,
            {},
          );

          if (result.error) {
            throw result.error;
          }

          return { data: result.data as any };
        } catch (error) {
          return { error: error as any };
        }
      },
    }),
  }),
});

export const {
  useLoginMutation,
  useLogoutMutation,
  useCreateUserMutation,
  useGetUserIdQuery,
  useEditAreNotificationsEnabledMutation,
  useEditUserOnboardingStepMutation,
  useGetClientOverviewQuery,
  useGetSubscriptionSummariesByUserIdQuery,
  useGetAllSubscriptionProfitsQuery,
  useGetBotQuery,
  useUpdateFeatureFlagMutation,
  useFetchHeikinCandlesQuery,
  useGetAvailableBalanceQuery,
  useGetAllAvailableBalancesFromExchangesQuery,
  useUpdateSubscriptionBalanceMutation,
  useUpdateSubscriptionStateMutation,
  useRemoveSubscriptionMutation,
  useUpdateSubscriptionPairMutation,
  useGetUserExchangesQuery,
  useCheckExchangeConnectionMutation,
  useAddExchangeConnectionMutation,
  useRemoveExchangeConnectionMutation,
  useGetTopBotsQuery,
  useGetUserAvailableCreditsQuery,
  useAddSubscriptionMutation,
  useGetBotIdsQuery,
  useCalculateSingleProfitCalculationQuery,
  useCalculateCurrentSingleProfitCalculationQuery,
  useStartComplexParamsCalculationMutation,
  useGetExchangeQueuesQuery,
  useGetCandleFetcherQueuesQuery,
  useGetSubscriptionDetailsQuery,
  useGetAllPredefinedBasketsQuery,
  useGetAllCopyMeBasketsQuery,
  useGetCopyMeConfigQuery,
  useSetUserCopyMeMutation,
  useSetBasketCopyMeMutation,
  useAddBasketMutation,
  useGetRsiSignalsQuery,
} = api;
